import type {
  AssetPerformanceDto,
  DashboardDto,
  InsightsSyncStatusDto,
  PublicReportInfoDto,
  ReportLinkDto,
  ReportScheduleDto,
  ReportScheduleRunDto,
  AccountSyncStatusDto,
  AdAccountDto,
  AssetListDto,
  AudienceListDto,
  CreationOptionsDto,
  CreativeAssetDto,
  DraftDto,
  PublicationDto,
  TemplateDto,
  CampaignAccountDto,
  CampaignEntityDetailDto,
  CampaignLevel,
  CampaignListDto,
  TablePrefsDto,
  AssignedUserDto,
  AuditLogDto,
  AuthProvidersDto,
  BusinessDetailDto,
  BusinessDto,
  ClientDto,
  DataDeletionStatusDto,
  InviteDto,
  InvitePreviewDto,
  MeDto,
  MemberDto,
  MetaConnectionDto,
  MetaStatusDto,
  NotificationsDto,
  PageDto,
  Paginated,
  PixelDto,
  SyncJobDto,
  WorkspaceDto,
} from "@iagenix/shared";
import { queryOptions } from "@tanstack/react-query";
import { api } from "./api.js";

/** `?clientId=` opcional nas listagens de ativos (filtro por cliente das telas). */
function withClient(path: string, clientId?: string): string {
  return clientId ? `${path}?clientId=${encodeURIComponent(clientId)}` : path;
}

export const queries = {
  authProviders: () =>
    queryOptions({
      queryKey: ["auth", "providers"],
      queryFn: () => api<AuthProvidersDto>("/api/auth/providers"),
      staleTime: Infinity,
    }),
  me: () =>
    queryOptions({
      queryKey: ["me"],
      queryFn: () => api<MeDto>("/api/me"),
      retry: false,
    }),
  workspaces: () =>
    queryOptions({
      queryKey: ["workspaces"],
      queryFn: () => api<WorkspaceDto[]>("/api/workspaces"),
    }),
  workspace: (id: string) =>
    queryOptions({
      queryKey: ["workspaces", id],
      queryFn: () => api<WorkspaceDto>(`/api/workspaces/${id}`),
    }),
  members: (id: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "members"],
      queryFn: () => api<MemberDto[]>(`/api/workspaces/${id}/members`),
    }),
  invites: (id: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "invites"],
      queryFn: () => api<InviteDto[]>(`/api/workspaces/${id}/invites`),
    }),
  auditLogs: (id: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "audit-logs"],
      queryFn: () => api<Paginated<AuditLogDto>>(`/api/workspaces/${id}/audit-logs`),
    }),
  metaStatus: (id: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "meta", "status"],
      queryFn: () => api<MetaStatusDto>(`/api/workspaces/${id}/meta/status`),
      refetchInterval: (q) => (q.state.data?.syncing ? 3_000 : 60_000),
      refetchIntervalInBackground: true,
    }),
  metaConnections: (id: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "meta", "connections"],
      queryFn: () => api<MetaConnectionDto[]>(`/api/workspaces/${id}/meta/connections`),
      refetchInterval: (q) =>
        q.state.data?.some(
          (c) => c.lastJob?.status === "pendente" || c.lastJob?.status === "executando",
        )
          ? 2_000
          : false,
      refetchIntervalInBackground: true,
    }),
  metaSyncJobs: (id: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "meta", "sync-jobs"],
      queryFn: () => api<SyncJobDto[]>(`/api/workspaces/${id}/meta/sync-jobs`),
    }),
  businesses: (id: string, clientId?: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "meta", "businesses", clientId ?? "todos"],
      queryFn: () =>
        api<BusinessDto[]>(withClient(`/api/workspaces/${id}/meta/businesses`, clientId)),
    }),
  businessDetail: (id: string, businessId: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "meta", "businesses", "detalhe", businessId],
      queryFn: () => api<BusinessDetailDto>(`/api/workspaces/${id}/meta/businesses/${businessId}`),
    }),
  adAccounts: (id: string, clientId?: string, includeHidden = false) =>
    queryOptions({
      queryKey: ["workspaces", id, "meta", "ad-accounts", clientId ?? "todos", includeHidden],
      queryFn: () => {
        const path = withClient(`/api/workspaces/${id}/meta/ad-accounts`, clientId);
        return api<AdAccountDto[]>(
          includeHidden ? `${path}${path.includes("?") ? "&" : "?"}ocultas=1` : path,
        );
      },
    }),
  adAccountUsers: (id: string, adAccountId: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "meta", "ad-accounts", "pessoas", adAccountId],
      queryFn: () =>
        api<AssignedUserDto[]>(`/api/workspaces/${id}/meta/ad-accounts/${adAccountId}/users`),
    }),
  pages: (id: string, clientId?: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "meta", "pages", clientId ?? "todos"],
      queryFn: () => api<PageDto[]>(withClient(`/api/workspaces/${id}/meta/pages`, clientId)),
    }),
  pixels: (id: string, clientId?: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "meta", "pixels", clientId ?? "todos"],
      queryFn: () => api<PixelDto[]>(withClient(`/api/workspaces/${id}/meta/pixels`, clientId)),
    }),
  clients: (id: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "clients"],
      queryFn: () => api<ClientDto[]>(`/api/workspaces/${id}/clients`),
    }),
  notifications: (id: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "notifications"],
      queryFn: () => api<NotificationsDto>(`/api/workspaces/${id}/notifications`),
      refetchInterval: 60_000,
    }),
  // ---- Fase 4: campanhas ----
  campaignAccounts: (id: string, clientId?: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "campaigns", "accounts", clientId ?? "todos"],
      queryFn: () =>
        api<CampaignAccountDto[]>(withClient(`/api/workspaces/${id}/campaigns/accounts`, clientId)),
    }),
  campaignSyncStatus: (id: string, account: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "campaigns", "sync", account],
      queryFn: () =>
        api<AccountSyncStatusDto>(
          `/api/workspaces/${id}/campaigns/accounts/${account}/sync-status`,
        ),
      refetchInterval: (q) => (q.state.data?.running ? 2_000 : 60_000),
      enabled: account.length > 0,
    }),
  campaignList: (
    id: string,
    account: string,
    level: CampaignLevel,
    params: Record<string, string>,
  ) =>
    queryOptions({
      queryKey: ["workspaces", id, "campaigns", "list", account, level, params],
      queryFn: () => {
        const qs = new URLSearchParams(params).toString();
        return api<CampaignListDto>(
          `/api/workspaces/${id}/campaigns/accounts/${account}/${level}${qs ? `?${qs}` : ""}`,
        );
      },
      refetchInterval: (q) => (q.state.data?.sync.running ? 3_000 : false),
      placeholderData: (prev) => prev,
      enabled: account.length > 0,
    }),
  campaignDetail: (id: string, level: CampaignLevel, metaId: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "campaigns", "detail", level, metaId],
      queryFn: () =>
        api<CampaignEntityDetailDto>(`/api/workspaces/${id}/campaigns/${level}/${metaId}`),
      enabled: metaId.length > 0,
    }),
  tablePrefs: (id: string, key: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "campaigns", "prefs", key],
      queryFn: () =>
        api<TablePrefsDto>(
          `/api/workspaces/${id}/campaigns/table-prefs/${encodeURIComponent(key)}`,
        ),
      staleTime: Infinity,
    }),
  // ---- Fase 5: criação ----
  creatives: (id: string, params: Record<string, string> = {}) =>
    queryOptions({
      queryKey: ["workspaces", id, "creatives", params],
      queryFn: () => {
        const qs = new URLSearchParams(params).toString();
        return api<AssetListDto>(`/api/workspaces/${id}/creatives${qs ? `?${qs}` : ""}`);
      },
      placeholderData: (prev) => prev,
    }),
  creative: (id: string, assetId: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "creatives", "item", assetId],
      queryFn: () => api<CreativeAssetDto>(`/api/workspaces/${id}/creatives/${assetId}`),
      enabled: assetId.length > 0,
    }),
  audiences: (id: string, account: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "audiences", account],
      queryFn: () => api<AudienceListDto>(`/api/workspaces/${id}/audiences/accounts/${account}`),
      refetchInterval: (q) => (q.state.data?.running ? 2_000 : false),
      enabled: account.length > 0,
    }),
  creationOptions: (id: string, account: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "creation", "options", account],
      queryFn: () =>
        api<CreationOptionsDto>(`/api/workspaces/${id}/creation/accounts/${account}/options`),
      enabled: account.length > 0,
    }),
  drafts: (id: string, account?: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "creation", "drafts", account ?? "todas"],
      queryFn: () =>
        api<DraftDto[]>(
          `/api/workspaces/${id}/creation/drafts${account ? `?adAccountMetaId=${account}` : ""}`,
        ),
    }),
  draft: (id: string, draftId: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "creation", "draft", draftId],
      queryFn: () => api<DraftDto>(`/api/workspaces/${id}/creation/drafts/${draftId}`),
      enabled: draftId.length > 0,
    }),
  publications: (id: string, account?: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "creation", "publications", account ?? "todas"],
      queryFn: () =>
        api<PublicationDto[]>(
          `/api/workspaces/${id}/creation/publications${account ? `?adAccountMetaId=${account}` : ""}`,
        ),
    }),
  publication: (id: string, publicationId: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "creation", "publication", publicationId],
      queryFn: () =>
        api<PublicationDto>(`/api/workspaces/${id}/creation/publications/${publicationId}`),
      refetchInterval: (q) => (q.state.data?.status === "executando" ? 1_500 : false),
      enabled: publicationId.length > 0,
    }),
  templates: (id: string, q = "") =>
    queryOptions({
      queryKey: ["workspaces", id, "creation", "templates", q],
      queryFn: () =>
        api<TemplateDto[]>(
          `/api/workspaces/${id}/creation/templates${q ? `?q=${encodeURIComponent(q)}` : ""}`,
        ),
    }),
  // ---- Fase 6: relatórios ----
  dashboard: (id: string, params: Record<string, string>) =>
    queryOptions({
      queryKey: ["workspaces", id, "reports", "dashboard", params],
      queryFn: () =>
        api<DashboardDto>(
          `/api/workspaces/${id}/reports/dashboard?${new URLSearchParams(params).toString()}`,
        ),
      placeholderData: (prev) => prev,
    }),
  insightsStatus: (id: string, account: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "reports", "insights-status", account],
      queryFn: () =>
        api<InsightsSyncStatusDto>(
          `/api/workspaces/${id}/reports/accounts/${account}/insights-status`,
        ),
      refetchInterval: (q) => (q.state.data?.running ? 2_000 : false),
      enabled: account.length > 0,
    }),
  reportLinks: (id: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "reports", "links"],
      queryFn: () => api<ReportLinkDto[]>(`/api/workspaces/${id}/reports/links`),
    }),
  reportSchedules: (id: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "reports", "schedules"],
      queryFn: () => api<ReportScheduleDto[]>(`/api/workspaces/${id}/reports/schedules`),
    }),
  reportScheduleRuns: (id: string, scheduleId: string) =>
    queryOptions({
      queryKey: ["workspaces", id, "reports", "schedules", scheduleId, "runs"],
      queryFn: () =>
        api<ReportScheduleRunDto[]>(`/api/workspaces/${id}/reports/schedules/${scheduleId}/runs`),
      enabled: scheduleId.length > 0,
    }),
  assetPerformance: (id: string, assetId: string, preset = "30d") =>
    queryOptions({
      queryKey: ["workspaces", id, "reports", "creative", assetId, preset],
      queryFn: () =>
        api<AssetPerformanceDto>(
          `/api/workspaces/${id}/reports/creatives/${assetId}/performance?preset=${preset}`,
        ),
      enabled: assetId.length > 0,
    }),
  publicReportInfo: (token: string) =>
    queryOptions({
      queryKey: ["public-report", token],
      queryFn: () => api<PublicReportInfoDto>(`/api/public/reports/${encodeURIComponent(token)}`),
      retry: false,
      enabled: token.length > 0,
    }),
  dataDeletionStatus: (code: string) =>
    queryOptions({
      queryKey: ["data-deletion", code],
      queryFn: () =>
        api<DataDeletionStatusDto>(`/api/meta/data-deletion/${encodeURIComponent(code)}`),
      retry: false,
      enabled: code.length > 0,
    }),
  invitePreview: (token: string) =>
    queryOptions({
      queryKey: ["invites", token],
      queryFn: () => api<InvitePreviewDto>(`/api/invites/${token}`),
      retry: false,
    }),
};
